import { supabase } from "./supabase";

// ─────────────────────────────────────────────────────────────────────────────
// Badges — server truth (Round 7). The signed-in user's earned badges and the
// per-badge earning events are read from `user_badges` / `user_badge_events`
// (RLS owner-select). Badge definitions come from `BADGE_DEFS` in
// `@nrl/shared`; this module never defines them and never writes reward state
// (SEC-3 — the client has no award path). History failures preserve awards;
// award failures return the empty shape. Both failures are logged.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Empty badges shape — the Badges tab's zero state and the fallback on error.
 * Returns a fresh copy each call so callers never share a mutable Set.
 */
export function EMPTY_BADGES() {
  return {
    season: new Set(),
    allTime: new Set(),
    awards: [],
    events: [],
    historyUnavailable: false,
  };
}

/**
 * Split a flat list of `user_badges` award rows into the season and all-time
 * id sets the Badges tab consumes. PURE: no Supabase, no mutation of the input.
 *
 * @param {Array<{badge_id: string, season_year: number}>} awards
 * @param {number} seasonYear
 * @returns {{season: Set<string>, allTime: Set<string>}}
 */
export function deriveBadgeSets(awards, seasonYear) {
  const out = { season: new Set(), allTime: new Set() };
  if (!Array.isArray(awards)) return out;
  for (const r of awards) {
    if (!r || r.badge_id == null) continue;
    out.allTime.add(r.badge_id);
    if (r.season_year === seasonYear) out.season.add(r.badge_id);
  }
  return out;
}

/**
 * Fetch the signed-in user's own badges + earning events (RLS-scoped). Returns
 * `{ season: Set<id>, allTime: Set<id>, awards: [...], events: [...] }` where
 * `events` is `[{ badgeId, seasonYear, at, fixture: { round, matchDate, homeRef,
 * awayRef, isOrigin }|null, playerName: string|null }]` sorted newest-first; on
 * award errors return `EMPTY_BADGES()`. History errors preserve awards with
 * `historyUnavailable: true` and empty events. Both errors warn. Display-only.
 *
 * @param {number} seasonYear
 */
export async function fetchMyBadges(seasonYear) {
  let awardsRows;
  let eventsRows = [];
  let historyUnavailable = false;
  try {
    const awardsRes = await supabase
      .from("user_badges")
      .select("badge_id,season_year,earned_at,fixture_id,player_id");
    if (awardsRes.error) throw awardsRes.error;
    awardsRows = Array.isArray(awardsRes.data) ? awardsRes.data : [];
  } catch (err) {
    console.warn("[badges] could not load badges:", err?.message || err);
    return EMPTY_BADGES();
  }
  try {
    const eventsRes = await supabase
      .from("user_badge_events")
      .select(
        "badge_id,season_year,created_at,fixture_id,player_id,fixtures(round,match_date,home_ref,away_ref,is_origin),players(name)",
      );
    if (eventsRes.error) throw eventsRes.error;
    eventsRows = Array.isArray(eventsRes.data) ? eventsRes.data : [];
  } catch (err) {
    historyUnavailable = true;
    console.warn("[badges] could not load user_badge_events:", err?.message || err);
  }

  const sets = deriveBadgeSets(awardsRows, seasonYear);
  const awards = awardsRows.map((r) => ({
    badgeId: r.badge_id,
    seasonYear: r.season_year,
    earnedAt: r.earned_at,
    fixtureId: r.fixture_id ?? null,
    playerId: r.player_id ?? null,
  }));

  const events = eventsRows
    .map((r) => {
      const fx = r.fixtures;
      const fixture =
        fx && typeof fx === "object"
          ? {
              round: fx.round ?? null,
              matchDate: fx.match_date ?? null,
              homeRef: fx.home_ref ?? null,
              awayRef: fx.away_ref ?? null,
              isOrigin: Boolean(fx.is_origin),
            }
          : null;
      const playerName =
        r.players && typeof r.players === "object" ? r.players.name ?? null : null;
      return {
        badgeId: r.badge_id,
        seasonYear: r.season_year,
        at: r.created_at,
        fixture,
        playerName,
      };
    })
    .sort((a, b) => (b.at < a.at ? -1 : b.at > a.at ? 1 : 0));

  return {
    season: sets.season,
    allTime: sets.allTime,
    awards,
    events,
    historyUnavailable,
  };
}
